import { useEffect, useRef, useState } from "react";

import { getAreaClustersInBounds, getAreaGroupsInBounds } from "@/api/areaCluster";

/**
 * 지도에 뜨는 상권 이름표를 배율에 따라 갈아 끼운다.
 *
 * 칩 하나에 두 가지 일을 시키면 어떤 기준을 세워도 반쪽이 된다. 그래서 나눈다.
 *
 *   ~15      큰 이름   "어디로 갈까"  — 홍대입구 · 건대입구 · 신촌. 넓은 게 맞다.
 *   15~16.5  세부      "그 안 어디"   — 연남동 · 명물거리. 그 동네로 들어왔을 때.
 *   16.5~    이름표 없음 — 개별 핀이 주인공
 *
 * 화면을 옮길 때마다 부르지 않는다. 같은 범위·같은 단계면 그대로 쓰고,
 * 결과는 잠깐 기억해 둔다(지도를 앞뒤로 움직일 때 깜빡이지 않게).
 */
const GROUP_MAX_ZOOM = 15; // 이보다 넓으면 큰 이름
const DETAIL_MAX_ZOOM = 16.5; // 이보다 넓으면 세부, 넘으면 이름표 없음
const CACHE_TTL_MS = 3 * 60 * 1000;
const CACHE_MAX = 40;

const boundsKey = (bounds, level) =>
  [
    level,
    Number(bounds.south).toFixed(3),
    Number(bounds.west).toFixed(3),
    Number(bounds.north).toFixed(3),
    Number(bounds.east).toFixed(3),
  ].join(",");

// `groupOnly` 는 **큰 이름 단계만** 쓴다는 뜻이다(세부 단계 없음).
// 2026-08-09 모바일을 켤 때 이렇게 시작했다 — 세부 상권은 아직 자동으로 붙은 이름이
// 겹쳐서(홍대 일대에 "홍대" 3개, "홍대입구역" 2개) 그대로 내보내면 고르기가 어렵다.
// 어드민에서 사람이 이름을 확정하는 만큼 이 손잡이를 풀면 된다.
const resolveLevel = (zoom, groupOnly) => {
  if (!Number.isFinite(zoom)) return null;
  if (zoom < GROUP_MAX_ZOOM) return "group";
  if (groupOnly) return null;
  return zoom < DETAIL_MAX_ZOOM ? "cluster" : null;
};

export const useAreaChips = ({ bounds, zoom, enabled = true, groupOnly = false }) => {
  const [chips, setChips] = useState([]);
  const level = resolveLevel(zoom, groupOnly);
  const cacheRef = useRef(new Map());
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !bounds || !Number.isFinite(zoom)) return;

    const level = resolveLevel(zoom, groupOnly);
    if (!level) {
      setChips([]);
      return;
    }
    const key = boundsKey(bounds, level);
    const cached = cacheRef.current.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setChips(cached.items);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    // ⚠️ 지도를 한 번 끄는 동안 이 훅이 예닐곱 번 불린다(카메라가 몇 번에 나눠 도착한다).
    //    예전에는 그때마다 조회가 나가서, 끌 때마다 같은 성격의 요청이 6~9건씩 나갔다
    //    (2026-08-10 실측). 멈춘 화면에서 **한 번만** 나가게 기다렸다 부른다.
    const timer = setTimeout(() => {

      const params = {
        south: bounds.south,
        west: bounds.west,
        north: bounds.north,
        east: bounds.east,
        limit: level === "group" ? 70 : 90,
      };

      const load = level === "group" ? getAreaGroupsInBounds : getAreaClustersInBounds;

      load(params)
        .then((items) => {
          if (requestId !== requestIdRef.current) return;
          const list = Array.isArray(items) ? items : [];
          cacheRef.current.set(key, { at: Date.now(), items: list });
          if (cacheRef.current.size > CACHE_MAX) {
            const oldest = cacheRef.current.keys().next().value;
            if (oldest) cacheRef.current.delete(oldest);
          }
          setChips(list);
        })
        .catch(() => {
          // 이름표를 못 받아도 지도는 그대로 쓴다. 핀은 따로 온다.
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [bounds, zoom, enabled, groupOnly]);

  // 큰 이름을 누르면 세부 단계로, 세부를 누르면 핀 단계로 간다.
  // 바탕 지도 타일이 14까지뿐이라 더 깊이 들어가면 흐려진다 — 필요한 만큼만 간다.
  const clickZoom = level === "group" ? GROUP_MAX_ZOOM + 0.6 : DETAIL_MAX_ZOOM + 0.7;

  return { chips, level, clickZoom };
};

export const AREA_CHIP_MAX_ZOOM = DETAIL_MAX_ZOOM;
